import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Stack,
  Text,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconArrowRight,
  IconExternalLink,
} from "@tabler/icons-react";
import * as pdfjsLib from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";
import LegacyPdfPage from "./LegacyPdfPage";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

interface LegacyPdfViewerProps {
  url: string;
  label: string;
  onRetry: () => void;
}

export default function LegacyPdfViewer({
  url,
  label,
  onRetry,
}: LegacyPdfViewerProps) {
  const [document, setDocument] = useState<PDFDocumentProxy | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [failed, setFailed] = useState(false);
  const totalPages = useMemo(() => document?.numPages ?? 0, [document]);

  const handlePrevious = useCallback(
    () => setPageNumber((page) => Math.max(1, page - 1)),
    []
  );
  const handleNext = useCallback(
    () => setPageNumber((page) => Math.min(totalPages, page + 1)),
    [totalPages]
  );

  useEffect(() => {
    let cancelled = false;
    const task = pdfjsLib.getDocument(url);
    void task.promise
      .then((loaded) => {
        if (!cancelled) setDocument(loaded);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      void task.destroy();
    };
  }, [url]);

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Group gap="xs">
          <Button
            variant="default"
            aria-label="Previous document page"
            onClick={handlePrevious}
            disabled={pageNumber <= 1 || !document}
            leftSection={<IconArrowLeft size={16} />}
          >
            <Box component="span" visibleFrom="sm">
              Previous
            </Box>
          </Button>
          <Text size="sm" aria-live="polite">
            Page {pageNumber} of {totalPages || "…"}
          </Text>
          <Button
            variant="default"
            aria-label="Next document page"
            onClick={handleNext}
            disabled={!document || pageNumber >= totalPages}
            rightSection={<IconArrowRight size={16} />}
          >
            <Box component="span" visibleFrom="sm">
              Next
            </Box>
          </Button>
        </Group>
        <Button
          component="a"
          href={url}
          target="_blank"
          rel="noreferrer"
          variant="subtle"
          leftSection={<IconExternalLink size={16} />}
        >
          Open original
        </Button>
      </Group>
      {failed ? (
        <Alert color="red" title="Preview unavailable" role="alert">
          <Stack gap="sm" align="flex-start">
            <Text size="sm">The original document could not be loaded.</Text>
            <Button variant="light" onClick={onRetry}>
              Retry preview
            </Button>
          </Stack>
        </Alert>
      ) : document ? (
        <LegacyPdfPage
          key={pageNumber}
          document={document}
          pageNumber={pageNumber}
          label={label}
        />
      ) : (
        <Center mih={300}>
          <Loader aria-label="Loading original document" />
        </Center>
      )}
    </Stack>
  );
}
